import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Printer } from "lucide-react";
import { api, errMsg, API, setTenantCurrency } from "@/lib/api";
import { DocumentView, StatusBadge, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";

export default function PublicQuote() {
  const { token } = useParams();
  const { data: data, refetch, error, isLoading } = useQuery({ queryKey: ["public-quote", token], queryFn: () => api.get(`/public/quotes/${token}`).then((r) => r.data), retry: false });
  useEffect(() => { if (data) setTenantCurrency(data.quote?.currency || data.business?.currency); }, [data]);
  const [busy, setBusy] = useState(false);
  if (isLoading) return <Loading full />;
  if (error) return <div className="min-h-screen grid place-items-center text-slate-500">Quote not found.</div>;
  if (!data) return null;
  const { quote: q, customer, business } = data;
  const act = async (kind) => {
    setBusy(true);
    try { await api.post(`/public/quotes/${token}/${kind}`); await refetch(); toast.success(kind === "accept" ? "Quote accepted. Thank you!" : "Quote declined."); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const open = ["sent", "viewed", "draft"].includes(q.status);
  return (
    <div className="min-h-screen bg-slate-100 py-6 px-3 sm:px-6" data-testid="public-quote-page">
      <div className="max-w-3xl mx-auto space-y-4">
        {q.status === "accepted" && <div className="rounded-xl bg-emerald-600 text-white p-4 flex items-center gap-3 no-print" data-testid="quote-accepted-banner"><CheckCircle2 /> <div><div className="font-bold">Quote accepted</div><div className="text-sm opacity-90">{business.business_name} will be in touch to confirm your service date.</div></div></div>}
        {q.status === "declined" && <div className="rounded-xl bg-slate-700 text-white p-4 flex items-center gap-3 no-print" data-testid="quote-declined-banner"><XCircle /> <div className="font-bold">Quote declined</div></div>}
        <DocumentView kind="quote" doc={q} customer={customer} business={business} badge={<StatusBadge status={q.status} />} />
        {open && (
          <div className="bg-white rounded-2xl border p-5 sm:p-6 no-print fade-up" data-testid="quote-response-card">
            <div className="text-center mb-4"><div className="font-display text-2xl font-bold">Ready to move forward?</div><p className="text-sm text-slate-500">Accept this quote and {business.business_name} will schedule your service.</p></div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button disabled={busy} onClick={() => act("accept")} className="btn-orange h-14 px-10 text-lg" data-testid="accept-quote-button"><CheckCircle2 size={20} /> Accept Quote</Button>
              <Button disabled={busy} variant="outline" onClick={() => act("decline")} className="h-14 px-8" data-testid="decline-quote-button"><XCircle size={18} /> Decline</Button>
            </div>
          </div>
        )}
        <div className="flex justify-center gap-3 no-print">
          <button onClick={() => window.print()} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1" data-testid="print-quote-button"><Printer size={14} /> Print</button>
          <a href={`${API}/public/quotes/${token}/pdf`} target="_blank" rel="noreferrer" className="text-sm text-slate-500 hover:text-slate-900" data-testid="public-quote-pdf-link">Download PDF</a>
        </div>
      </div>
    </div>
  );
}
